<template>
  <div v-loading="loading">
    <template v-if="state === 'ready'">
      <el-form-item v-if="showRate" :label="text.rate" :label-width="labelWidth">
        <el-input v-model="rate" maxlength="3" :disabled="busy" @input="rate = String($event).replace(/\D/g, '')">
          <template slot="append">%</template>
        </el-input>
      </el-form-item>
    </template>
    <p v-if="state === 'error'" role="alert">{{ text.loadError }} <el-button type="text" :disabled="busy" @click="load">{{ text.reload }}</el-button></p>
    <p v-if="message" role="alert">{{ message }} <el-button v-if="uncertain" type="text" :disabled="busy" @click="$emit('requery')">{{ text.requery }}</el-button></p>
  </div>
</template>
<script>
import BigNumber from 'bignumber.js'
import { call } from './api'
import labels from './settingsLabels'

export default {
  props: { userId: [String, Number], syncMode: Boolean, showRate: { type: Boolean, default: true }, labelWidth: { type: String, default: '140px' } },
  data: () => ({ state: 'loading', loading: false, busy: false, channel: null, rate: '', request: 0, message: '', uncertain: false, contractSaved: null }),
  computed: {
    text() { return labels },
    isRoot() { return this.channel && (this.channel.p_agent == null || this.channel.p_agent === '') },
  },
  watch: { userId() { this.contractSaved = null; this.message = ''; this.uncertain = false; this.load() } },
  mounted() { this.load() },
  beforeDestroy() { this.request++ },
  methods: {
    setState(state) { this.state = state; this.$emit('state', state) },
    async load() {
      const request = ++this.request
      this.loading = true; this.channel = null; this.setState('loading')
      try {
        const metadata = await call(true, '/metadata')
        if (request !== this.request) return
        if (!metadata || !Object.prototype.hasOwnProperty.call(metadata, 'enabledAt')) throw new Error()
        if (!metadata.enabledAt) { this.setState('legacy'); return }
        if (!/^\d+$/.test(String(this.userId || ''))) { this.setState('waiting'); return }
        const rows = await call(true, '/channels', { tenantUserId: this.userId })
        if (request !== this.request) return
        if (!Array.isArray(rows) || rows.length !== 1) throw new Error()
        this.channel = rows[0]; this.rate = new BigNumber(this.channel.spot_rate).times(100).toFixed()
        this.setState('ready')
      } catch (e) { if (request === this.request) this.setState('error') }
      finally { if (request === this.request) this.loading = false }
    },
    prepare(params) {
      if (this.state !== 'ready' || this.uncertain) throw new Error(this.text.notReady)
      // Only the original mode/cycle dialog can change the root's spot mode.
      // Rate-only entry points and subordinate partners never overwrite it.
      let mode
      if (this.syncMode && this.isRoot) {
        if (Number(params.rebateType) === 1) mode = 0
        else if (Number(params.rebateType) === 2 && Number(params.rebateCycle) === 2) mode = 1
        else throw new Error(this.text.unsupportedMode)
      }
      if (this.showRate && (!/^\d+$/.test(this.rate) || new BigNumber(this.rate).gt(100))) throw new Error(this.text.integer)
      // Disabling commission preserves the saved rate, including hidden edits.
      const rate = this.showRate ? new BigNumber(this.rate).div(100) : new BigNumber(this.channel.spot_rate)
      const modeChanged = mode != null && mode !== Number(this.channel.mode)
      if (rate.eq(this.channel.spot_rate) && !modeChanged) return null
      return { channelId: this.channel.id, rate: rate.toFixed(), ...(modeChanged ? { mode } : {}) }
    },
    async saveWithContract(params, saveContract) {
      if (this.busy) return false
      let payload
      try { payload = this.prepare(params) } catch (e) { this.message = e.message; return false }
      this.busy = true; this.$emit('busy', true); this.message = ''
      const fingerprint = JSON.stringify(params)
      try {
        if (this.contractSaved !== fingerprint) {
          let result
          try { result = await saveContract(params) } catch (e) { this.message = this.text.contractUnknown; this.uncertain = true; return false }
          if (!result || result.code == null) { this.message = this.text.contractUnknown; this.uncertain = true; return false }
          if (result.code !== 0) { this.message = this.text.contractFailed; return false }
          this.contractSaved = fingerprint
          this.$emit('refresh')
        }
        if (payload) {
          try {
            const result = await call(true, '/rate', payload, true)
            if (result !== true) throw new Error()
          } catch (e) {
            this.message = e.businessFailure ? this.text.spotFailed : this.text.spotUnknown
            this.uncertain = !e.businessFailure
            return false
          }
          this.channel.spot_rate = payload.rate
          if (payload.mode != null) this.channel.mode = payload.mode
          this.$emit('refresh')
        }
        this.$message.success(this.text.saved)
        return true
      } finally { this.busy = false; this.$emit('busy', false) }
    },
  },
}
</script>
